import { Link } from 'react-router-dom'
import styled from 'styled-components'

const FooterWrapper = styled.footer`
  border-top: 1px solid var(--color-border);
  padding: 64px 24px 32px;
  background: var(--color-bg);
`

const FooterInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
`

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 48px;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const FooterBrand = styled.div`
  h3 {
    font-size: 1.25rem;
    font-weight: 700;

    span {
      background: linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    color: var(--color-text-secondary);
    font-size: 0.9rem;
    line-height: 1.6;
    max-width: 320px;
  }
`

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`

const FooterLogoIcon = styled.svg`
  width: 28px;
  height: 28px;
  color: var(--color-text);
  flex-shrink: 0;
`

const FooterColumn = styled.div`
  h4 {
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--color-text-muted);
    margin-bottom: 16px;
  }
`

const FooterLink = styled(Link)`
  display: block;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 10px;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-text);
  }
`

const ExternalLink = styled.a`
  display: block;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 10px;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-text);
  }
`

const FooterBottom = styled.div`
  border-top: 1px solid var(--color-border);
  padding-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--color-text-muted);
  font-size: 0.8rem;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`

export default function Footer() {
  return (
    <FooterWrapper>
      <FooterInner>
        <FooterGrid>
          <FooterBrand>
            <BrandRow>
              <FooterLogoIcon viewBox="0 0 1166 1148" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M846.2 343.8c-122.5-134.8-329.1-153.4-470.1-36.1-19.2 15.8-36.7 33.6-52.4 52.9-3.9 4.8-7.8 9.8-11.5 14.8l-19.2-24.2c2.4-3.1 4.9-6.4 7.3-9.4 145.9-181.8 410.4-189.5 567.9-18.3 7.2 8.1 14.2 16.4 20.8 25 .2 0-14.5 19.3-18.5 25-.2.2-.3.4-.3.4-3.8-5.2-7.7-10.4-11.7-15.3-4-5-8.1-9.9-12.4-14.7z" fill="currentColor"/>
                <path d="M532.8 673.5l49.9-83.9 3.3 0 50.6 85.5" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M382.1 440.2c66.3 115.3 134.6 229.6 203 343.6 67.7-114.2 136.6-228 202-343.6" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M264.3 411.1c55 .5 146.2-.2 200.1 0 8.9.2 17.6.3 26.6 1.3 1.7.2 4 .6 5.7 1.2 3.3 1.1 6.5 3.3 8.3 6.3 8.9 14 16.4 27.8 24.2 42.2 19.7 36.3 37.9 73.4 58.4 109.1l2.9 4.7c.2.4.5.8.7 1.1.6.9 1.5 2.2 2.1 3 .4.6 1.1 1.5 1.3 1.9l.3.6.2.4c.6 1.1.8 1.5.8 1.6 0 0 0-.2-1-1.5-2.5-3-6.3-4.7-10.3-4.7l-4.1.5c-2.5.7-4.8 2.3-6.4 4.3-.4.6-.6.8-.9 1.4l.2-.5c.6-1.3 1.5-2.8 2.4-3.9.6-.9 1.7-2.4 2.3-3.3.2-.3.5-.8.7-1.1l2.9-4.6c24.8-42.6 46.4-87.1 71-129.9 4-7 8.2-14 12.8-21.1 1.7-2.6 4.1-4.4 6.8-5.6 1.2-.5 2.4-.9 3.6-1.2 5.3-1.1 10.4-1.2 15.5-1.5 22.6-.9 49-.4 71.7-.3 19.4.5 120.4-.4 141.7-.5 5.1 0 11.1 0 15.8 0 1.8 2.1 2.9 4.7 4.7 8.3 3.4 7.6 8.8 18.6 6.9 21.5-5.6 0-20.1.2-27.3.2-22.1 0-122.7 1.2-142.2.5-24.6 0-52.5-.8-76.8.7-1.2 0-2.6.2-3.5.4.1 0 .3 0 .4-.1h.4c1.3-.5 2.5-1.2 3.6-2l3.6-3.8c-29.1 46.7-52.4 96.9-80 144.6-2.4 4-5.7 9.8-8.4 13.6l-2.1 3-.5.7c.1-.1.4-.5.5-.7l.2-.3v-.2c.4-.5.2-.2-.2.7-2.3 5.2-8.9 10.4-15.5 10.2-4.6 0-8.9-2-12-5.3-1.9-2-3.3-4.7-3.8-5.9l.2.4c0 0 .4.6.6.9.1.2 0 0 0 0-.9-1.3-2.6-3.8-3.5-5.1-2.1-3.3-5.4-8.8-7.3-12.2-27.2-47.8-50.1-98.1-78.9-144.9l3.5 3.8c1.3 1 2.8 1.8 4.4 2.2-3.1-.4-6.7-.6-10.1-.8-22-.9-48.4-.5-70.6-.4-37.4.5-132.9 0-170.2-.2-1.9-2.9 3.4-13.9 6.8-21.5 1.9-3.6 2.9-6.2 4.7-8.3 4.8 0 10.7 0 15.8 0z" fill="currentColor"/>
                <path d="M248.4 519.2c-15.9 151.5 95.2 296.7 234 348.1 150.7 56.4 305.4-9.3 384.5-146.3 51-87.6 68.3-199.8 29.2-295-1.5-3.3-4.7-7.9-2.8-10.6 2-2.7 9.8-3.4 28.8-4.7 13.5 28.4 19.8 59.9 23.4 90.9 23 221.6-163.2 444.1-394.2 413.2-188.5-23.3-352.8-205.9-332.7-399 2.8-26.8 7.9-53.8 18.1-78.6 5.1 1 26.2-.8 29.8 2.6.3.3.5.6.5.9-9.5 25.4-15.7 51.6-18.6 78.5z" fill="currentColor"/>
              </FooterLogoIcon>
              <h3>Valyss <span>for Business</span></h3>
            </BrandRow>
            <p>AI agents for service businesses. Built on the same platform that powers DoMoreWorld.</p>
          </FooterBrand>
          <FooterColumn>
            <h4>Services</h4>
            <FooterLink to="/services">Consulting</FooterLink>
            <FooterLink to="/platform">Self-Serve Platform</FooterLink>
            <FooterLink to="/contact">Discovery Call</FooterLink>
          </FooterColumn>
          <FooterColumn>
            <h4>Company</h4>
            <FooterLink to="/about">About Us</FooterLink>
            <ExternalLink href="https://app.domoreworld.com" target="_blank" rel="noopener noreferrer">See Our Platform Live</ExternalLink>
            <ExternalLink href="https://shop.valyss.com" target="_blank" rel="noopener noreferrer">Valyss Shop (Coming Soon)</ExternalLink>
          </FooterColumn>
          <FooterColumn>
            <h4>Connect</h4>
            <ExternalLink href="mailto:contact@valyss.com">contact@valyss.com</ExternalLink>
            <ExternalLink href="tel:+16464705147">(646) 470-5147</ExternalLink>
            <ExternalLink href="https://www.linkedin.com/in/coybyron/" target="_blank" rel="noopener noreferrer">LinkedIn</ExternalLink>
          </FooterColumn>
        </FooterGrid>
        <FooterBottom>
          <span>&copy; {new Date().getFullYear()} Valyss. All rights reserved.</span>
          <span>Silicon Valley, CA</span>
        </FooterBottom>
      </FooterInner>
    </FooterWrapper>
  )
}
